import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import CheckUtils from "../../../../utilities/check-utils";
import { Address } from "../../../../infrastructure/http/modules/cart/cart.models";
import TranslationsContext from "../../../../infrastructure/context/translations/translations-context";
import TranslationManager from "../../../../infrastructure/translation-manager";
import { TranslationKeys } from "../../../../infrastructure/const";
import UserContext from "../../../../infrastructure/context/user/user.context";
import AddressSelectDropdown from "./components/address-select-dropdown";
import NewAddressForm from "./components/new-address-form";
import { IAddress } from "../../../../infrastructure/context/user/user.context.interface";
import ConfigurationContext from "../../../../infrastructure/context/configuration/configuration.context";

type CheckoutAddressFormProps = {
  displayEmailAddress: boolean;
  address: Address;
  onUpdate: (address: Address) => void;
  displayValidationMessages: boolean;
  defaultAddressId: string | null;
  supportedCountries: Map<string, string>;
  apiErrorResponse: Array<string>;
  saveAddress: boolean;
  onSaveAddressChanged: (saveAddressValue: boolean) => void;
  isInvoiceAddress: boolean;
  fields?: Array<keyof Address>;
};

export type AddressState = {
  id: string;
  firstName: any;
  middleName: any;
  lastName: any;
  email: any;
  company: any;
  addressApiText: string,
  addressLine1: any;
  addressLine2: any;
  postalCode: any;
  city: any;
  phoneNumber: any;
  countryCode: string;
  county: string;
  validationErrors: string[];
  selectedAddressId: string;
  supportedCountries: any;
  apiErrorResponse: string[];
};

const defaultFields: Array<keyof Address> = [
  "firstName",
  "middleName",
  "lastName",
  "addressLine1",
  "addressLine2",
  "postalCode",
  "city",
  "phoneNumber",
  "countryCode",
  "company",
];

const defaultRequiredFields: Array<keyof Address> = [
  "firstName",
  "lastName",
  "addressLine1",
  "postalCode",
  "city",
  "phoneNumber",
];

export interface CheckoutAddressFormRefs {
  isComponentFilledCorrectly: () => boolean;
}

const CheckoutAddressForm = forwardRef(
  (
    {
      displayEmailAddress,
      address,
      onUpdate,
      displayValidationMessages,
      defaultAddressId,
      supportedCountries,
      apiErrorResponse,
      saveAddress,
      onSaveAddressChanged,
      isInvoiceAddress,
      fields,
    }: CheckoutAddressFormProps,
    ref: React.Ref<CheckoutAddressFormRefs>
  ) => {
    const isInitialized = React.useRef<boolean>(false);
    const translationsContext = useContext(TranslationsContext);
    const translationManager = new TranslationManager(translationsContext);
    const userContext = useContext(UserContext);
    const configurationContext = useContext(ConfigurationContext);

    const newAddressId = "-1";

    const hasAddressBook = useMemo(() => {
      return (
        userContext &&
        userContext.user &&
        userContext.user.userProfile &&
        userContext.user.userProfile.addressBook &&
        userContext.user.userProfile.addressBook.length > 0
      );
    }, [userContext]);

    const formFields = useMemo(() => {
      const f = fields || defaultFields;
      if (isInvoiceAddress && !f.includes("email")) {
        return [...f, "email"] as Array<keyof Address>;
      }
      return f;
    }, [fields, isInvoiceAddress]);

    const requiredFields = useMemo((): Array<keyof Address> => {
      let fields = defaultRequiredFields;
      if (isInvoiceAddress) {
        fields = [...defaultRequiredFields, "email"];
      }
      if (
        configurationContext.isFeatureFlagEnabled("addressStreet2IsRequired")
      ) {
        fields = [...fields, "addressLine2"];
      }
      return fields;
    }, [isInvoiceAddress, configurationContext]);

    const idPrefix = isInvoiceAddress ? "invoiceForm_" : "deliveryForm_";

    const getInitialValidationErrors = useCallback(
      (state) => {
        const errors: string[] = [];
        requiredFields.forEach((key) => {
          if (CheckUtils.isNullOrEmptyString(state[key])) {
            errors.push(key.toString());
          }
        });
        return errors;
      },
      [requiredFields]
    );

    const getInitialState = useCallback((): AddressState => {
      const countriesMap = CheckUtils.isNullObject(supportedCountries)
        ? new Map()
        : supportedCountries;

      const initialAddress: IAddress | Address = hasAddressBook
        ? userContext.user.userProfile.addressBook.find(
            (x) => x.id === defaultAddressId
          ) || address
        : address;

      const email = address.email
        ? address.email
        : userContext.user.userProfile.email;

      const initialState: AddressState = {
        id: defaultAddressId ? defaultAddressId : newAddressId,
        firstName: initialAddress.firstName,
        middleName: initialAddress.middleName,
        lastName: initialAddress.lastName,
        email: isInvoiceAddress ? email : "",
        company: initialAddress.company,
        addressApiText: initialAddress.addressApiText,
        addressLine1:
          "addressLine1" in initialAddress
            ? initialAddress.addressLine1
            : initialAddress.streetAddress1,
        addressLine2:
          "addressLine2" in initialAddress
            ? initialAddress.addressLine2
            : initialAddress.streetAddress2,
        postalCode: initialAddress.postalCode,
        city: initialAddress.city,
        phoneNumber:
          "phoneNumber" in initialAddress
            ? initialAddress.phoneNumber
            : initialAddress.telephone,
        countryCode:
          Object.keys(countriesMap).length > 0
            ? Object.keys(countriesMap)[0]
            : "",
        county: initialAddress.county,
        validationErrors: [],
        selectedAddressId: defaultAddressId ? defaultAddressId : newAddressId,
        supportedCountries: countriesMap,
        apiErrorResponse: [],
      };
      initialState.validationErrors = getInitialValidationErrors(initialState);
      return initialState;
    }, [
      supportedCountries,
      hasAddressBook,
      userContext.user.userProfile.addressBook,
      userContext.user.userProfile.email,
      address,
      defaultAddressId,
      isInvoiceAddress,
      getInitialValidationErrors,
    ]);

    const [state, setState] = useState<AddressState>(() => getInitialState());

    const onAddressSelectionChange = useCallback(
      (selectedAddressId: string, addressList: Address[]) => {
        setState((prevState) => ({
          ...prevState,
          selectedAddressId: selectedAddressId,
        }));

        if (
          CheckUtils.isNullOrEmptyString(selectedAddressId) ||
          selectedAddressId === newAddressId ||
          CheckUtils.isNullOrEmptyArray(addressList)
        ) {
          onUpdate(getInitialState());
          return;
        }

        const selectedAddress = addressList.find(
          (x) => x.id === selectedAddressId
        );
        if (!selectedAddress) {
          return;
        }
        onUpdate(selectedAddress);
      },
      [newAddressId, getInitialState, onUpdate]
    );

    useEffect(() => {
      if (!isInitialized.current) {
        setState((prevState) => {
          const newState = getInitialState();
          onUpdate(newState);
          isInitialized.current = true;
          return { ...prevState, ...newState };
        });
      }
    }, [address, getInitialState, isInvoiceAddress, onUpdate]);

    useEffect(() => {
      if (
        JSON.stringify(apiErrorResponse) !==
        JSON.stringify(state.apiErrorResponse)
      ) {
        setState((prevState) => ({
          ...prevState,
          apiErrorResponse: apiErrorResponse,
        }));
      }
    }, [apiErrorResponse, state.apiErrorResponse]);

    function onInputValueChange(key: string, value: any, isValid: boolean) {
      setState((prevState) => {
        let validationErrors = [...prevState.validationErrors];
        if (isValid) {
          // Remove the key from errors if validation passed
          validationErrors = validationErrors.filter((x) => x !== key);
        } else if (!validationErrors.includes(key)) {
          // Add the key to errors if validation failed and key isn't already in the errors
          validationErrors.push(key);
        }

        const newState = {
          ...prevState,
          validationErrors,
          [key]: value,
        };

        onUpdate({
          ...prevState,
          [key]: value,
        });

        return newState;
      });
    }

    function onAddressApiValueChange(address: Address) {
      setState((prevState) => {

        prevState.addressApiText = address.addressApiText;
        prevState.addressLine1 = address.addressLine1;
        prevState.addressLine2 = address.addressLine2;
        prevState.city = address.city;
        prevState.postalCode = address.postalCode;
        prevState.county = address.county;

        onUpdate(prevState);

        return prevState;
      });
    }

    useImperativeHandle(ref, () => ({
      isComponentFilledCorrectly(): boolean {
        return (
          CheckUtils.isNullOrEmptyArray(state.validationErrors) &&
          CheckUtils.isNullOrEmptyArray(state.apiErrorResponse)
        );
      },
    }));

    return (
      <div>
        <>
          {hasAddressBook && (
            <AddressSelectDropdown
              idPrefix={idPrefix}
              selectedAddressId={state.selectedAddressId}
              newAddressId={newAddressId}
              selectAddressText={translationManager.getTranslation(
                TranslationKeys.CHECKOUT_SELECT_ADDRESS
              )}
              newAddressText={translationManager.getTranslation(
                TranslationKeys.NEWADDRESS
              )}
              onSelectionChange={(selectedAddressId, addressList) => {
                onAddressSelectionChange(selectedAddressId, addressList);
              }}
            />
          )}

          {(!hasAddressBook || state.selectedAddressId === newAddressId) && (
            <NewAddressForm
              idPrefix={idPrefix}
              formFields={formFields}
              requiredFields={requiredFields}
              displayEmailAddress={displayEmailAddress}
              displayValidationMessages={displayValidationMessages}
              onInputValueChange={onInputValueChange}
              onAddressApiValueChange={onAddressApiValueChange}
              saveAddress={saveAddress}
              onSaveAddressChanged={onSaveAddressChanged}
              state={state}
            />
          )}
        </>
      </div>
    );
  }
);

export default CheckoutAddressForm;
